import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import Loader from 'components/Loader';
import BreadcrumbsComponent, { breadcrumbsWatchlistParser } from 'components/Breadcrumbs';
import { PAGE_TYPE_WATCHLIST } from 'helpers/ContentHelper';
import WatchlistSEO from 'helpers/SEO/SeoObjects/Watchlist.json';
import MobileEditSelect from './components/MobileEditSelect';
import WatchlistResult from './components/WatchlistResult';
import { fetchFavorites, fetchNextPage, getFavoriteData } from './WatchlistHelper';
import NotFound from './components/NotFound';

import Style from './Watchlist.module.scss';

const Watchlist = ({
  account,
  breakpoint,
  favoritesManager,
  profileFavorites,
  anonymousAccount,
}) => {
  const [isMobileEditActive, setIsMobileEditActive] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState('');
  const [isWatchlistEmpty, setIsWatchlistEmpty] = useState(false);
  const watchlistCount = watchlistItems?.length;
  const mobileCheckedCount = checkedItems?.length;
  const { watchlistPage_header: watchlistHeader } = window.Language;

  useEffect(() => {
    fetchFavorites({
      account: account || anonymousAccount,
      profileFavorites,
      setIsLoading,
      setIsWatchlistEmpty,
      setNextUrl,
      setWatchlistItems,
    });
  }, [account, anonymousAccount, profileFavorites]);

  useEffect(() => {
    if (isMobileEditActive) {
      handleAnalytics({
        event: Events.BUTTON_LINK_SELECTED,
        payload: {
          page_button_text: 'edit',
          page_name: 'delta:web:watchlist',
          page_type: PAGE_TYPE_WATCHLIST,
        },
      });
    }
  }, [isMobileEditActive]);

  const handleSelectAll = () => {
    const selectAllList = [];
    watchlistItems.map(item => {
      const favoriteData = getFavoriteData({ item });
      selectAllList.push(favoriteData);
    });
    setCheckedItems(() => [...selectAllList]);
  };

  const handleDelete = () => {
    if (checkedItems) {
      favoritesManager.deleteFavorites({
        favoriteList: checkedItems,
        favoritesDetails: favoritesManager?.favoritesDetails,
      });
      const updatedList = [];
      watchlistItems.forEach(item => {
        const isMatched = !!checkedItems.filter(fav => fav.showID === item.showCode).length;
        if (!isMatched) updatedList.push(item);
      });
      setWatchlistItems(() => [...updatedList]);
      if (nextUrl) {
        fetchNextPage({ account, nextUrl, setWatchlistItems, setNextUrl });
      } else if (updatedList.length <= 0) {
        setIsWatchlistEmpty(true);
      }
      setCheckedItems(() => []);
    }
  };

  return (
    <section className={Style.container}>
      {setHelmetTags(WatchlistSEO)}
      <h2 className={Style.headline}>{watchlistHeader}</h2>
      {isWatchlistEmpty && <NotFound />}
      {!isWatchlistEmpty && (
        <>
          <MobileEditSelect
            handleSelectAll={handleSelectAll}
            isMobileEditActive={isMobileEditActive}
            mobileCheckedCount={mobileCheckedCount}
            setCheckedItems={setCheckedItems}
            setIsMobileEditActive={setIsMobileEditActive}
            watchlistCount={watchlistCount}
          />
        </>
      )}
      {!isLoading ? (
        <WatchlistResult
          account={account}
          breakpoint={breakpoint}
          checkedItems={checkedItems}
          favoritesManager={favoritesManager}
          isMobileEditActive={isMobileEditActive}
          isWatchlistEmpty={isWatchlistEmpty}
          nextUrl={nextUrl}
          setCheckedItems={setCheckedItems}
          setNextUrl={setNextUrl}
          setWatchlistItems={setWatchlistItems}
          watchlistCount={watchlistCount}
          watchlistItems={watchlistItems}
        />
      ) : (
        <Loader />
      )}
      <DeleteButton
        checkedItems={checkedItems}
        handleDelete={handleDelete}
        isMobileEditActive={isMobileEditActive}
        mobileCheckedCount={mobileCheckedCount}
      />
      <BreadcrumbsComponent data={breadcrumbsWatchlistParser()} customStyle={Style.breadcrumbs} />
    </section>
  );
};

const DeleteButton = ({ checkedItems, handleDelete, isMobileEditActive, mobileCheckedCount }) => {
  if (!mobileCheckedCount || !isMobileEditActive) return null;
  return (
    <div onClick={handleDelete} className={Style.mobileDeleteButton}>
      Delete ({checkedItems.length} Selected)
    </div>
  );
};

DeleteButton.propTypes = {
  checkedItems: PropTypes.arrayOf(PropTypes.shape({})),
  handleDelete: PropTypes.func,
  isMobileEditActive: PropTypes.bool,
  mobileCheckedCount: PropTypes.number,
};

Watchlist.propTypes = {
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  profileFavorites: PropTypes.shape({}),
};

export default Watchlist;
